import React, { useState } from "react";
import {
  FileText,
  Sparkles,
  SearchCheck,
  ArrowRight,
  HelpCircle,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import WebUtils from "../../../utils/WebUtils";
import Utils from "../../../utils/Utils";
import Loader from "../../Loader";
import ResumeListModal from "./optimizeResume/modals/ResumeListModal";
import { createFromScratchApi } from "../../../api/b2b-candidate";
import { notification, Tooltip } from "antd";
import MatchResumeModal from "./optimizeResume/modals/MatchResumeModal";

const cards = [
  {
    title: "Create from scratch",
    description:
      "Build a professional, ATS friendly resume from the ground up with AI guidance.",
    action: "Create with AI",
    icon: FileText,
    active: true,
    id: 0,
  },
  {
    title: "Polish your resume",
    description:
      "Upload your resume and let AI enhance clarity, impact, and keyword relevance.",
    action: "Refine with AI",
    icon: Sparkles,
    active: true,
    id: 1,
  },
  {
    title: "Match resume to job",
    description:
      "Improve your chances by tailoring your resume to the selected job role.",
    action: "Analyze & Optimize",
    icon: SearchCheck,
    active: true,
    id: 2,
  },
];

const OptimizeResume = ({ moduleInfo }) => {
  const navigate = useNavigate();
  const userDetail = Utils.getUserDetail();
  const profileDetail = Utils.getProfileData();

  const [loading, setLoading] = useState(false);
  const [resumeListFlag, setResumeListFlag] = useState(false);
  const [matchResumeFlag, setMatchResumeFlag] = useState(false);

  const createFromScratch = () => {
    setLoading(true);
    const url = `${createFromScratchApi}`;
    const body = {
      userIdentifier: userDetail?.userIdentifier,
      organizationId: userDetail?.organizationId,
      firstName: profileDetail?.firstName,
      lastName: profileDetail?.lastName,
    };

    WebUtils.httpOperations(url, body, "POST")
      .then((result) => {
        setLoading(false);
        if (
          result.data.resultStatusInfo.resultCode.toLowerCase() === "success"
        ) {
          const resumeIdentifier = result.data?.data?.resumeIdentifier;
          navigate(`/editResume?resumeIdentifier=${resumeIdentifier}`);
        } else {
          notification.error({
            message: "Failed",
            description: result?.data?.resultStatusInfo?.message,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const handleNavigate = (id) => {
    if (id === 0) {
      createFromScratch();
    } else if (id === 1) {
      setResumeListFlag(true);
    } else if (id === 2) {
      setMatchResumeFlag(true);
    }
  };
  return (
    <>
      {loading && <Loader />}
      {resumeListFlag && (
        <ResumeListModal
          resumeListFlag={resumeListFlag}
          setResumeListFlag={setResumeListFlag}
          setLoading={setLoading}
        />
      )}
      {matchResumeFlag && (
        <MatchResumeModal
          setLoading={setLoading}
          matchResumeFlag={matchResumeFlag}
          setMatchResumeFlag={setMatchResumeFlag}
        />
      )}
      <div className="w-full max-w-6xl mx-auto px-6 py-8 mt-5">
        {userDetail?.userRole === "JOBDRONA_CANDIDATE" && (
          <div className="w-full flex justify-end items-center gap-y-2 gap-x-3">
            <div className="flex gap-1 items-center justify-center text-[.83rem] font-semibold border border-[#FEC84B] bg-[#FFFAEB] text-[#0F1B23] rounded-full hover-effect px-3 py-[.2rem]">
              Resume Match Credits:{" "}
              {moduleInfo?.aiResumeMatchCredits > 0 ? (
                <span className="text-green-600 font-mono text-[.98rem] font-semibold">
                  {moduleInfo?.aiResumeMatchCredits}
                </span>
              ) : (
                <span className="text-red-500 font-mono text-[1rem] font-semibold">
                  {moduleInfo?.aiResumeMatchCredits}
                </span>
              )}
              <Tooltip
                title={
                  <>
                    <div className="font-medium text-center text-[.88rem] text-orangish">
                      Credits Left:{" "}
                      <span className="font-bold ">
                        {moduleInfo?.aiResumeMatchCredits}
                      </span>
                    </div>
                    <div className="text-white text-center">
                      This shows the number of credits you have left to create
                      comparison and AI content generation.
                    </div>
                  </>
                }
                overlayInnerStyle={{
                  padding: "10px 14px",
                  background: "#0F1B23",
                }}
              >
                <HelpCircle size={19} className="ml-1 cursor-pointer" />
              </Tooltip>
            </div>
            <Link to={"/credits"}>
              <button className="py-[.35rem] px-[.9rem] text-[.9rem] btn-orange">
                Buy Credits
              </button>
            </Link>
          </div>
        )}
        <div className="text-center mb-7 mt-4">
          <h2 className="text-[1.25rem] font-bold text-gray-900">
            Create a professional, ATS ready resume in minutes.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 md:grid:cols-2 gap-6">
          {cards.map((card, index) => {
            const Icon = card.icon;

            return (
              <div
                key={index}
                className={`rounded-2xl border p-6 transition-all 
                ${
                  card.active
                    ? "border-amber-400 bg-amber-50 zoom-card"
                    : "border-[#D0D5DD] bg-white hover:border-gray-300"
                }`}
              >
                <div
                  className={`w-9 h-9 border border-[#DFDFDF] shadow-sm rounded-lg flex items-center justify-center mb-3
                  ${
                    card.active
                      ? "bg-amber-100 text-amber-600"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  <Icon size={18} />
                </div>

                <h3 className="text-[1rem] font-semibold text-[#101828] mb-1">
                  {card.title}
                </h3>

                <p className="text-[.85rem] font-normal text-[#475467] leading-relaxed mb-4">
                  {card.description}
                </p>

                <button
                  onClick={() => handleNavigate(card.id)}
                  disabled={card.disabled}
                  className={`flex items-center gap-2 font-medium text-[.85rem] w-auto transition-all duration-200
                  ${
                    card.active && "text-amber-600 hover:text-amber-500"
                    // : "text-[#667085] hover:text-amber-500"
                  } ${card.disabled ? "cursor-not-allowed" : ""}`}
                >
                  {card.action}
                  <ArrowRight size={16} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default OptimizeResume;
